import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { dataFetch } from '../../api';
import { sendEmail } from '../../services/emailService';

const { FiX, FiSend, FiLoader } = FiIcons;

const CreateSimulationDrawer = ({ isOpen, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    name: '',
    template: 'Password Expiry Notice',
    targetType: 'All', // All, Departments, Specific
    departments: [],
    learners: []
  });
  
  const [employees, setEmployees] = useState([]);
  const [dbDepartments, setDbDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFetching(true);
      Promise.all([
        dataFetch('read/employees'),
        dataFetch('read/departments')
      ]).then(([empRes, deptRes]) => {
        if (empRes?.status === 'success') setEmployees(empRes.data || []);
        if (deptRes?.status === 'success') setDbDepartments(deptRes.data || []);
        setFetching(false);
      });
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    let targets = [];
    if (form.targetType === 'All') {
      targets = employees;
    } else if (form.targetType === 'Departments') {
      targets = employees.filter(emp => form.departments.includes(emp.department));
    } else {
      targets = employees.filter(emp => form.learners.includes(emp.id));
    }

    if (targets.length === 0) {
      alert("No recipients selected. Please adjust your target audience.");
      setLoading(false);
      return;
    }

    const simPayload = {
      name: form.name,
      type: form.template,
      training_module_id: '',
      date: new Date().toISOString().slice(0, 19).replace('T', ' '),
      sent: targets.length,
      opened: 0,
      clicked: 0,
      reported: 0,
      target_audience: form.targetType,
      recipient_type: form.targetType === 'Departments' ? form.departments.join(', ') : 'All',
      user_id: localStorage.getItem('user_id') || ''
    };

    const simRes = await dataFetch('create/phishing_simulations', {
      method: 'POST',
      body: JSON.stringify(simPayload)
    });

    if (simRes?.status === 'success') {
      const simId = simRes.data.id;
      
      // Create recipient tracking records
      const recipientsPayload = targets.map(emp => ({
        campaign_id: String(simId),
        employee_id: String(emp.id),
        email: emp.email,
        status: 'Sent',
        tracking_token: `${simId}-${emp.id}-${Date.now().toString(36)}`,
        clicked: 0,
        reported: 0,
        user_id: localStorage.getItem('user_id') || ''
      }));

      await dataFetch('bulk/create/phishing_recipients', {
        method: 'POST',
        body: JSON.stringify({ records: recipientsPayload })
      });

      // Send phishing emails
      for (const emp of targets) {
        if (!emp.email) continue;
        
        // Match the token used during bulk creation
        const recipientRecord = recipientsPayload.find(r => r.employee_id === String(emp.id));
        const token = recipientRecord.tracking_token;
        
        const inviteLink = `${window.location.origin}/phishing-awareness?simId=${simId}&userId=${emp.id}&token=${token}`;
        const reportLink = `${window.location.origin}/phishing-awareness?simId=${simId}&userId=${emp.id}&token=${token}&action=report`;
        
        let messageBody = form.template === 'Password Expiry Notice' 
            ? 'Your corporate password is set to expire soon. Please click the link below to verify your account and keep your current password.'
            : 'Please review and accept the latest IT security policies by clicking the link below. Failure to accept may result in restricted access.';

        // Append the report instructions to the email body
        messageBody += `\n\n---\nIf you believe this email is suspicious, please report it here:\n${reportLink}`;
        
        await sendEmail({
          toName: `${emp.firstName} ${emp.lastName}`,
          toEmail: emp.email,
          subject: form.template === 'Password Expiry Notice' ? 'URGENT: Password Expiring in 24 Hours' : 'Required: IT Security Policy Update',
          message: messageBody,
          inviteLink,
          firstName: emp.firstName,
          organizationName: 'IT Support Team',
          role: emp.jobTitle || 'Employee',
          expirationDate: '24 Hours',
          templateId: import.meta.env.VITE_PHISHING_EMAIL_TEMPLATE || 'template_mvymp1i'
        });
      }

      onSuccess();
      onClose();
      setForm({ name: '', template: 'Password Expiry Notice', targetType: 'All', departments: [], learners: [] });
    } else {
      alert('Failed to create simulation');
    }
    setLoading(false);
  };

  const handleDeptToggle = (deptName) => {
    setForm(prev => {
      const exists = prev.departments.includes(deptName);
      return {
        ...prev,
        departments: exists ? prev.departments.filter(d => d !== deptName) : [...prev.departments, deptName]
      };
    });
  };

  const handleLearnerToggle = (id) => {
    setForm(prev => {
      const exists = prev.learners.includes(id);
      return {
        ...prev,
        learners: exists ? prev.learners.filter(l => l !== id) : [...prev.learners, id]
      };
    });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40"
            onClick={onClose}
          />
          <motion.div 
            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl z-50 flex flex-col border-l border-slate-200"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50">
              <h2 className="text-lg font-bold text-slate-800">Create Simulation</h2>
              <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-500">
                <SafeIcon icon={FiX} className="text-xl" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              {fetching ? (
                <div className="flex flex-col justify-center items-center h-40 space-y-3">
                  <SafeIcon icon={FiLoader} className="animate-spin text-3xl text-slate-400" />
                  <p className="text-sm text-slate-500">Loading resources...</p>
                </div>
              ) : (
                <form id="create-sim-form" onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Simulation Name</label>
                    <input 
                      required type="text" value={form.name} 
                      onChange={e => setForm({...form, name: e.target.value})} 
                      placeholder="e.g. Q3 Phishing Test"
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-200" 
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Phishing Template</label>
                    <select 
                      value={form.template} 
                      onChange={e => setForm({...form, template: e.target.value})} 
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-400 bg-white"
                    >
                      <option>Password Expiry Notice</option>
                      <option>IT Update Required</option>
                      <option>HR Policy Review</option>
                      <option>Unusual Sign-in Activity</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Target Audience</label>
                    <div className="flex gap-4">
                      {['All', 'Departments', 'Specific'].map(type => (
                        <label key={type} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                          <input 
                            type="radio" 
                            name="targetType" 
                            checked={form.targetType === type}
                            onChange={() => setForm({...form, targetType: type})}
                            className="text-slate-900 accent-slate-900"
                          />
                          {type === 'All' ? 'All Learners' : type === 'Departments' ? 'By Dept' : 'Specific'}
                        </label>
                      ))}
                    </div>
                  </div>

                  {form.targetType === 'Departments' && (
                    <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg max-h-48 overflow-y-auto space-y-2">
                      <p className="text-[10px] font-bold text-slate-400 mb-2 uppercase tracking-wider">Select Departments</p>
                      {dbDepartments.map(d => (
                        <label key={d.id} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={form.departments.includes(d.department_name)}
                            onChange={() => handleDeptToggle(d.department_name)}
                            className="accent-slate-900 rounded"
                          />
                          {d.department_name}
                        </label>
                      ))}
                      {dbDepartments.length === 0 && <p className="text-xs text-slate-400">No departments found.</p>}
                    </div>
                  )}

                  {form.targetType === 'Specific' && (
                    <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg max-h-48 overflow-y-auto space-y-2">
                      <p className="text-[10px] font-bold text-slate-400 mb-2 uppercase tracking-wider">Select Learners</p>
                      {employees.map(emp => (
                        <label key={emp.id} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={form.learners.includes(emp.id)}
                            onChange={() => handleLearnerToggle(emp.id)}
                            className="accent-slate-900 rounded"
                          />
                          {emp.firstName} {emp.lastName} <span className="text-xs text-slate-400 ml-1">({emp.email})</span>
                        </label>
                      ))}
                      {employees.length === 0 && <p className="text-xs text-slate-400">No learners found.</p>}
                    </div>
                  )}
                </form>
              )}
            </div>

            <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-end gap-3">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-200 rounded-lg transition-colors">
                Cancel
              </button>
              <button 
                type="submit" 
                form="create-sim-form" 
                disabled={loading || fetching} 
                className="px-5 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2 shadow-sm"
              >
                {loading ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiSend} />}
                Create & Send
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CreateSimulationDrawer;